import BaseApiService from './baseApiService';
import api from './api';

class ContractorMarketplaceService extends BaseApiService {
  constructor(apiClient = api) {
    super(apiClient, '/contractor');
  }

  /**
   * Get open projects from marketplace
   */
  async getOpenProjects(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
      if (filters.category) params.append('category', filters.category);
      if (filters.minBudget) params.append('minBudget', filters.minBudget);
      if (filters.maxBudget) params.append('maxBudget', filters.maxBudget);
      if (filters.location) params.append('location', filters.location);
      if (filters.city) params.append('city', filters.city);
      if (filters.projectType) params.append('projectType', filters.projectType);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.order) params.append('order', filters.order);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.coversTravel) params.append('coversTravel', 'true');
      
      const response = await this.apiClient.get(`${this.baseURL}/marketplace?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get marketplace projects error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get a single open project by id (for shareable deep links: ?project=<id>)
   */
  async getOpenProject(projectId) {
    try {
      const response = await this.apiClient.get(`${this.baseURL}/marketplace/${encodeURIComponent(projectId)}`);
      return response.data;
    } catch (error) {
      console.error('Get marketplace project error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get recommended projects for contractor
   */
  async getRecommendedProjects() {
    try {
      const response = await this.apiClient.get(`${this.baseURL}/marketplace/recommended`);
      return response.data;
    } catch (error) {
      console.error('Get recommended projects error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Submit a proposal
   */
  async submitProposal(proposalData) {
    try {
      const response = await this.apiClient.post(`${this.baseURL}/proposals`, proposalData);
      return response.data;
    } catch (error) {
      console.error('Submit proposal error:', error);
      throw error;
    }
  }

  /**
   * Request assessment/details before creating a fixed professional bid
   */
  async submitAssessmentRequest(requestData) {
    try {
      const response = await this.apiClient.post(`${this.baseURL}/assessment-requests`, requestData);
      return response.data;
    } catch (error) {
      console.error('Submit assessment request error:', error);
      throw error;
    }
  }

  /**
   * Amend an existing assessment/detail request.
   */
  async updateAssessmentRequest(requestId, requestData) {
    try {
      const response = await this.apiClient.put(`${this.baseURL}/assessment-requests/${requestId}`, requestData);
      return response.data;
    } catch (error) {
      console.error('Update assessment request error:', error);
      throw error;
    }
  }

  /**
   * Withdraw an existing assessment/detail request.
   */
  async withdrawAssessmentRequest(requestId) {
    try {
      const response = await this.apiClient.put(`${this.baseURL}/assessment-requests/${requestId}/withdraw`);
      return response.data;
    } catch (error) {
      console.error('Withdraw assessment request error:', error);
      throw error;
    }
  }

  /**
   * Accept one of the owner's proposed site-visit times — schedules the visit.
   */
  async acceptAssessmentWindow(requestId, visitWindow) {
    try {
      const response = await this.apiClient.put(`${this.baseURL}/assessment-requests/${requestId}/accept-window`, { visitWindow });
      return response.data;
    } catch (error) {
      console.error('Accept assessment window error:', error);
      throw error;
    }
  }

  /**
   * Mark the site visit complete with proof photos + a bid-by date.
   * formData: photos[] files + bidPromisedBy.
   */
  async completeAssessmentVisit(requestId, formData) {
    try {
      const response = await this.apiClient.post(
        `${this.baseURL}/assessment-requests/${requestId}/complete-visit`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      console.error('Complete assessment visit error:', error);
      throw error;
    }
  }

  /**
   * Get contractor's assessment/detail requests
   */
  async getMyAssessmentRequests(status = null) {
    try {
      const url = status
        ? `${this.baseURL}/assessment-requests?status=${status}`
        : `${this.baseURL}/assessment-requests`;
      const response = await this.apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Get assessment requests error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get contractor's proposals
   */
  async getMyProposals(status = null) {
    try {
      const url = status 
        ? `${this.baseURL}/proposals?status=${status}`
        : `${this.baseURL}/proposals`;
      const response = await this.apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Get proposals error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Update proposal (withdraw)
   */
  async updateProposal(proposalId, status, notes = null) {
    try {
      const response = await this.apiClient.put(`${this.baseURL}/proposals/${proposalId}`, {
        status,
        notes
      });
      return response.data;
    } catch (error) {
      console.error('Update proposal error:', error);
      throw error;
    }
  }

  /**
   * Save project for later
   */
  async saveProject(projectId, notes = null) {
    try {
      const payload = { projectId };
      if (notes) payload.notes = notes;

      const response = await this.apiClient.post(`${this.baseURL}/saved-projects`, payload);
      return response.data;
    } catch (error) {
      console.error('Save project error:', error);
      throw error;
    }
  }

  /**
   * Remove a saved project
   */
  async unsaveProject(projectId) {
    try {
      const response = await this.apiClient.delete(`${this.baseURL}/saved-projects/${encodeURIComponent(projectId)}`);
      return response.data;
    } catch (error) {
      console.error('Unsave project error:', error);
      throw error;
    }
  }

  /**
   * Get saved projects
   */
  async getSavedProjects() {
    try {
      const response = await this.apiClient.get(`${this.baseURL}/saved-projects`);
      return response.data;
    } catch (error) {
      console.error('Get saved projects error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get contractor portfolio
   */
  async getPortfolio() {
    try {
      const response = await this.apiClient.get(`${this.baseURL}/portfolio`);
      return response.data;
    } catch (error) {
      console.error('Get portfolio error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Add portfolio item
   */
  async addPortfolioItem(itemData) {
    try {
      const response = await this.apiClient.post(`${this.baseURL}/portfolio`, itemData);
      return response.data;
    } catch (error) {
      console.error('Add portfolio error:', error);
      throw error;
    }
  }

  /**
   * Update portfolio item
   */
  async updatePortfolioItem(itemId, updates) {
    try {
      const response = await this.apiClient.put(`${this.baseURL}/portfolio/${itemId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Update portfolio error:', error);
      throw error;
    }
  }

  /**
   * Delete portfolio item
   */
  async deletePortfolioItem(itemId) {
    try {
      const response = await this.apiClient.delete(`${this.baseURL}/portfolio/${itemId}`);
      return response.data;
    } catch (error) {
      console.error('Delete portfolio error:', error);
      throw error;
    }
  }

  /**
   * Get contractor preferences
   */
  async getPreferences() {
    try {
      const response = await this.apiClient.get(`${this.baseURL}/preferences`);
      return response.data;
    } catch (error) {
      console.error('Get preferences error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Update contractor preferences
   */
  async updatePreferences(preferences) {
    try {
      const response = await this.apiClient.put(`${this.baseURL}/preferences`, preferences);
      return response.data;
    } catch (error) {
      console.error('Update preferences error:', error);
      throw error;
    }
  }

  /**
   * Get contractor analytics
   */
  async getAnalytics(startDate = null, endDate = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const response = await this.apiClient.get(`${this.baseURL}/analytics?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get analytics error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get complete contractor profile
   */
  async getCompleteProfile() {
    try {
      const response = await this.apiClient.get(`${this.baseURL}/profile`);
      return response.data;
    } catch (error) {
      console.error('Get profile error:', error);
      return this.handleError(error);
    }
  }

  /**
   * Mock data for development
   */
  getMockMarketplaceProjects() {
    return {
      success: true,
      data: {
        projects: [
          {
            id: 'proj_001',
            title: '3 Bedroom House Construction',
            description: 'Modern family home with spacious living areas, located in Borrowdale',
            category: 'residential',
            project_type: 'house',
            location: 'Borrowdale, Harare',
            city: 'Harare',
            total_budget: 85000,
            currency: 'USD',
            status: 'open',
            is_open_to_bids: true,
            proposal_deadline: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
            client_first_name: 'Margaret',
            client_last_name: 'Chivero',
            diaspora_location: 'United Kingdom',
            proposal_count: 3,
            created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
          },
          {
            id: 'proj_002',
            title: 'Commercial Building Renovation',
            description: 'Full renovation of 2-story office building including electrical and plumbing',
            category: 'commercial',
            project_type: 'renovation',
            location: 'CBD, Harare',
            city: 'Harare',
            total_budget: 45000,
            currency: 'USD',
            status: 'open',
            is_open_to_bids: true,
            proposal_deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            client_first_name: 'Tatenda',
            client_last_name: 'Moyo',
            diaspora_location: 'South Africa',
            proposal_count: 5,
            created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
          },
          {
            id: 'proj_003',
            title: 'Swimming Pool Installation',
            description: 'Olympic-sized swimming pool with modern filtration system',
            category: 'residential',
            project_type: 'pool',
            location: 'Mt Pleasant, Harare',
            city: 'Harare',
            total_budget: 25000,
            currency: 'USD',
            status: 'open',
            is_open_to_bids: true,
            proposal_deadline: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
            client_first_name: 'James',
            client_last_name: 'Ndlovu',
            diaspora_location: 'United States',
            proposal_count: 2,
            created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
          }
        ],
        pagination: {
          page: 1,
          limit: 20,
          total: 3,
          totalPages: 1
        }
      }
    };
  }
}

// Export both the class and a default instance
export { ContractorMarketplaceService };
export default new ContractorMarketplaceService(api);
